import React from 'react';
import { SlidersHorizontal, X } from 'lucide-react';
import { Button, Chip } from '../ui';
import { useAppStore } from '../../context/store';
import { sizeCategories } from '../../data/cases';
import { formatDimension } from '../../utils/conversions';

export type SortOption = 'name-asc' | 'manufacturer-asc' | 'releaseYear-desc';

const sortOptions: { value: SortOption; label: string }[] = [
  { value: 'name-asc', label: 'Name A–Z' },
  { value: 'manufacturer-asc', label: 'Brand A–Z' },
  { value: 'releaseYear-desc', label: 'Newest first' },
];

interface FilterBarProps {
  stats: { total: number; byCategory: Record<string, number> };
  sort: SortOption;
  onSortChange: (sort: SortOption) => void;
}

interface ActiveFilter {
  key: string;
  label: string;
  onRemove: () => void;
}

export const FilterBar: React.FC<FilterBarProps> = ({ stats, sort, onSortChange }) => {
  const {
    filters,
    comparison,
    setFilters,
    resetFilters,
    toggleSizeCategory,
    toggleFormFactor,
    toggleManufacturer,
    toggleSidePanelType,
    setMinGpuLength,
    setMinCoolerHeight,
    setMinPsuLength,
    setMinRadiatorSize,
    setSearchQuery,
    setFilterPanelOpen,
  } = useAppStore();

  const unit = comparison.unitSystem === 'metric' ? 'mm' : 'in';

  const activeFilters: ActiveFilter[] = [
    ...(filters.searchQuery
      ? [{ key: 'q', label: `“${filters.searchQuery}”`, onRemove: () => setSearchQuery('') }]
      : []),
    ...filters.manufacturers.map((m) => ({ key: `m-${m}`, label: m, onRemove: () => toggleManufacturer(m) })),
    ...filters.formFactors.map((ff) => ({ key: `ff-${ff}`, label: ff, onRemove: () => toggleFormFactor(ff) })),
    ...filters.sidePanelTypes.map((t) => ({ key: `sp-${t}`, label: t, onRemove: () => toggleSidePanelType(t) })),
    ...(filters.minGpuLength > 0
      ? [{ key: 'gpu', label: `GPU ≥ ${formatDimension(filters.minGpuLength, unit)}`, onRemove: () => setMinGpuLength(0) }]
      : []),
    ...(filters.minCoolerHeight > 0
      ? [{ key: 'cooler', label: `Cooler ≥ ${formatDimension(filters.minCoolerHeight, unit)}`, onRemove: () => setMinCoolerHeight(0) }]
      : []),
    ...(filters.minPsuLength > 0
      ? [{ key: 'psu', label: `PSU ≥ ${formatDimension(filters.minPsuLength, unit)}`, onRemove: () => setMinPsuLength(0) }]
      : []),
    ...(filters.minRadiatorSize > 0
      ? [{ key: 'rad', label: `Radiator ≥ ${filters.minRadiatorSize} mm`, onRemove: () => setMinRadiatorSize(0) }]
      : []),
  ];

  // Everything that lives in the panel (size chips and search are visible on the page itself)
  const panelFilterCount = activeFilters.filter((f) => f.key !== 'q').length;

  return (
    <div className="sticky top-0 z-20 border-b border-line bg-canvas/95 backdrop-blur supports-[backdrop-filter]:bg-canvas/85">
      <div className="mx-auto flex max-w-7xl flex-col gap-3 px-4 py-3.5 sm:px-6">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
          <div role="group" aria-label="Case size" className="flex w-full gap-2 overflow-x-auto hide-scrollbar md:w-auto md:flex-1">
            <Chip
              active={filters.sizeCategories.length === 0}
              count={stats.total || undefined}
              onClick={() => setFilters({ sizeCategories: [] })}
            >
              All
            </Chip>
            {sizeCategories.map((size) => (
              <Chip
                key={size}
                active={filters.sizeCategories.includes(size)}
                count={stats.byCategory[size]}
                onClick={() => toggleSizeCategory(size)}
              >
                {size}
              </Chip>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <label className="flex items-center gap-2 text-sm">
              <span className="text-muted">Sort</span>
              <select
                value={sort}
                onChange={(e) => onSortChange(e.target.value as SortOption)}
                className="h-10 rounded-lg border border-line-strong bg-surface px-2.5 text-sm text-fg cursor-pointer focus:outline-none focus:border-accent"
              >
                {sortOptions.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </label>
            <Button variant="outline" onClick={() => setFilterPanelOpen(true)}>
              <SlidersHorizontal size={16} />
              Filters
              {panelFilterCount > 0 && (
                <span className="ml-0.5 rounded-full bg-accent px-1.5 font-mono text-xs text-accent-fg">
                  {panelFilterCount}
                </span>
              )}
            </Button>
          </div>
        </div>

        {activeFilters.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            {activeFilters.map((filter) => (
              <span
                key={filter.key}
                className="inline-flex h-8 items-center gap-1 rounded-full border border-accent/40 bg-accent-soft pl-3 pr-1 text-xs font-medium text-accent-ink"
              >
                {filter.label}
                <button
                  type="button"
                  onClick={filter.onRemove}
                  aria-label={`Remove filter ${filter.label}`}
                  className="inline-flex h-6 w-6 items-center justify-center rounded-full hover:bg-accent/15"
                >
                  <X size={12} />
                </button>
              </span>
            ))}
            <button
              type="button"
              onClick={resetFilters}
              className="h-8 px-2 text-xs font-medium text-muted hover:text-fg underline-offset-2 hover:underline"
            >
              Clear all
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
